import { useEffect, useMemo, useState } from 'react';

import { ApiClient, formatDate, formatMoney, useAsync, useMutation } from '@clientma/core';
import {
  Alert,
  Badge,
  Button,
  Card,
  Drawer,
  Field,
  Input,
  Select,
  Skeleton,
  Textarea,
  useToast,
} from '@clientma/ui';

import {
  providerApi,
  type ProviderPlanDetail,
  type ProviderPlanEntitlementInput,
  type ProviderPlanInput,
  type ProviderPlanPriceInput,
  type ProviderPlanRow,
} from '../api';

/**
 * The plan catalogue.
 *
 * Plans used to arrive through the seeder, so pricing a new country or launching a tier meant a
 * code change and a deployment. This is the same catalogue, maintained by the people who sell it.
 *
 * The screen leans hard on one rule the server enforces: a price is added, never edited. A
 * subscription records the exact price row it was sold on, so quietly rewriting one would
 * re-price existing customers at their next renewal. Rows that have been sold on say so and
 * offer "supersede" instead of "edit".
 */
export default function Plans({ api }: { api: ApiClient }) {
  const provider = useMemo(() => providerApi(api), [api]);

  const [editing, setEditing] = useState<{ id: string | null } | null>(null);
  const [search, setSearch] = useState('');

  const { data, isLoading, error, reload } = useAsync(
    (signal) => provider.plans(signal),
    [provider],
  );

  const plans = useMemo(() => {
    const all = data ?? [];
    const term = search.trim().toLowerCase();

    if (!term) return all;

    return all.filter((plan) =>
      [plan.name, plan.planKey, plan.applicationKey, plan.tier].some((field) =>
        field?.toLowerCase().includes(term),
      ),
    );
  }, [data, search]);

  if (isLoading) return <Skeleton height={280} />;

  if (error) {
    return (
      <Alert tone="danger" title="The plan catalogue could not be loaded">
        {error.message}
      </Alert>
    );
  }

  const onSale = plans.filter((p) => p.isActive).length;
  const unpriced = plans.filter((p) => p.priceCount === 0).length;

  return (
    <div className="cma-stack">
      <div className="provider-grid">
        <Tile label="Plans" value={plans.length} />
        <Tile label="On sale" value={onSale} />
        <Tile
          label="Without a price"
          value={unpriced}
          tone={unpriced ? 'warning' : undefined}
        />
        <Tile
          label="Paying subscriptions"
          value={plans.reduce((sum, p) => sum + p.subscriberCount, 0)}
        />
      </div>

      <div className="provider-toolbar">
        <Input
          type="search"
          value={search}
          placeholder="Search by name, key, product or tier…"
          aria-label="Search plans"
          onChange={(event) => setSearch(event.target.value)}
        />

        <Button variant="primary" onClick={() => setEditing({ id: null })}>
          New plan
        </Button>
      </div>

      {plans.length === 0 ? (
        <Alert tone="info" title="No plans">
          Nothing is on sale yet. Create a plan, give it a price, then put it on sale.
        </Alert>
      ) : (
        plans.map((plan) => (
          <PlanCard
            key={plan.id}
            api={api}
            plan={plan}
            onChanged={reload}
            onEdit={() => setEditing({ id: plan.id })}
          />
        ))
      )}

      {editing && (
        <PlanDrawer
          api={api}
          planId={editing.id}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void reload();
          }}
        />
      )}
    </div>
  );
}

function PlanCard({
  api,
  plan,
  onChanged,
  onEdit,
}: {
  api: ApiClient;
  plan: ProviderPlanRow;
  onChanged: () => void;
  onEdit: () => void;
}) {
  const provider = useMemo(() => providerApi(api), [api]);
  const toast = useToast();
  const [message, setMessage] = useState<string | null>(null);

  const toggle = useMutation(() => provider.setPlanActive(plan.id, !plan.isActive), {
    onSuccess: () => {
      toast.success(plan.isActive ? `${plan.name} taken off sale` : `${plan.name} is on sale`);
      onChanged();
    },
    onError: (failure) => setMessage(failure.message),
  });

  return (
    <Card>
      <div className="cma-stack cma-stack--tight">
        {message && <Alert tone="danger">{message}</Alert>}

        <div className="provider-actionrow">
          <div>
            <div className="provider-strong">{plan.name}</div>
            <div className="provider-muted">
              {plan.planKey} · {plan.tier}
              {plan.applicationKey ? ` · ${plan.applicationKey}` : ''}
            </div>
          </div>

          <div className="provider-chips">
            <Badge tone={plan.isActive ? 'success' : 'neutral'}>
              {plan.isActive ? 'On sale' : 'Off sale'}
            </Badge>

            {plan.priceCount === 0 && <Badge tone="warning">No price</Badge>}
            {plan.isBundle && <Badge tone="info">Bundle</Badge>}
            {plan.isAddOn && <Badge tone="info">Add-on</Badge>}
            {!plan.isPubliclyListed && <Badge tone="neutral">Unlisted</Badge>}
          </div>
        </div>

        <div>
          <div className="provider-linerow">
            <span className="provider-muted">Seats</span>
            <span>
              {plan.includedSeats} included
              {plan.maximumSeats == null ? ', no cap' : `, up to ${plan.maximumSeats}`}
            </span>
          </div>

          <div className="provider-linerow">
            <span className="provider-muted">Trial</span>
            <span>{plan.isTrialAvailable ? `${plan.trialDays} days` : 'None'}</span>
          </div>

          <div className="provider-linerow">
            <span className="provider-muted">Prices · entitlements</span>
            <span>
              {plan.priceCount} · {plan.entitlementCount}
            </span>
          </div>

          <div className="provider-linerow">
            <span className="provider-muted">AI credits a month</span>
            <span>
              {plan.aiCreditsPerMonth ? plan.aiCreditsPerMonth.toLocaleString() : 'Not included'}
            </span>
          </div>

          <div className="provider-linerow">
            <span className="provider-muted">Paying subscriptions</span>
            <span>{plan.subscriberCount}</span>
          </div>
        </div>

        <div className="provider-actionrow">
          {/* Taking a plan off sale stops new customers choosing it. It does not touch anybody
              already on it, whose term runs to its end on the price they hold. */}
          <span className="provider-muted">
            {plan.subscriberCount > 0
              ? `${plan.subscriberCount} subscription${
                  plan.subscriberCount === 1 ? '' : 's'
                } would keep running if this went off sale.`
              : 'Nobody is on this plan.'}
          </span>

          <div className="provider-chips">
            <Button size="sm" onClick={onEdit}>
              Open
            </Button>

            <Button
              size="sm"
              isLoading={toggle.isRunning}
              onClick={() => void toggle.run(undefined)}
            >
              {plan.isActive ? 'Take off sale' : 'Put on sale'}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}

// ────────────────────────────────────────────────────────────────── the editor

const emptyPlan: ProviderPlanInput = {
  planKey: '',
  name: '',
  description: '',
  tier: 'Starter',
  applicationKey: '',
  bundledApplicationKeys: '',
  isBundle: false,
  isAddOn: false,
  isPubliclyListed: true,
  displayOrder: 0,
  isTrialAvailable: true,
  trialDays: 14,
  trialSeatCap: null,
  trialRequiresPaymentMethod: false,
  includedSeats: 1,
  maximumSeats: null,
  highlightsJson: null,
};

function PlanDrawer({
  api,
  planId,
  onClose,
  onSaved,
}: {
  api: ApiClient;
  planId: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const provider = useMemo(() => providerApi(api), [api]);
  const toast = useToast();

  const [draft, setDraft] = useState<ProviderPlanInput>(emptyPlan);
  const [detail, setDetail] = useState<ProviderPlanDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'plan' | 'prices' | 'entitlements'>('plan');

  const loaded = useAsync(
    (signal) => (planId ? provider.plan(planId, signal) : Promise.resolve(null)),
    [provider, planId],
  );

  useEffect(() => {
    if (!loaded.data) return;

    const p = loaded.data;
    setDetail(p);
    setDraft({
      planKey: p.planKey,
      name: p.name,
      description: p.description ?? '',
      tier: p.tier,
      applicationKey: p.applicationKey ?? '',
      bundledApplicationKeys: p.bundledApplicationKeys ?? '',
      isBundle: p.isBundle,
      isAddOn: p.isAddOn,
      isPubliclyListed: p.isPubliclyListed,
      displayOrder: p.displayOrder,
      isTrialAvailable: p.isTrialAvailable,
      trialDays: p.trialDays,
      trialSeatCap: p.trialSeatCap ?? null,
      trialRequiresPaymentMethod: p.trialRequiresPaymentMethod,
      includedSeats: p.includedSeats,
      maximumSeats: p.maximumSeats ?? null,
      highlightsJson: p.highlightsJson ?? null,
    });
  }, [loaded.data]);

  const set = (patch: Partial<ProviderPlanInput>) =>
    setDraft((current) => ({ ...current, ...patch }));

  const save = useMutation(
    () =>
      planId
        ? provider.updatePlan(planId, draft).then(() => ({ id: planId }))
        : provider.createPlan(draft),
    {
      onSuccess: (result) => {
        toast.success(planId ? `${draft.name} saved` : `${draft.name} created, off sale until priced`);

        // A new plan stays open on its Prices tab: it cannot be sold until it has one, and
        // closing here would hide the only thing left to do.
        if (planId) onSaved();
        else void reopen(result.id);
      },
      onError: (failure) => setError(failure.message),
    },
  );

  const reopen = async (id: string) => {
    const fresh = await provider.plan(id);
    setDetail(fresh);
    setTab('prices');
  };

  const reloadDetail = async () => {
    if (!detail) return;
    setDetail(await provider.plan(detail.id));
  };

  return (
    <Drawer
      open
      title={planId || detail ? draft.name || 'Plan' : 'New plan'}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={save.isRunning}>
            Close
          </Button>

          {tab === 'plan' && (
            <Button
              variant="primary"
              isLoading={save.isRunning}
              onClick={() => {
                setError(null);
                void save.run(undefined);
              }}
            >
              {planId || detail ? 'Save' : 'Create'}
            </Button>
          )}
        </>
      }
    >
      {planId && loaded.isLoading ? (
        <Skeleton height={320} />
      ) : (
        <div className="cma-stack">
          {error && (
            <Alert tone="danger" title="That could not be saved">
              {error}
            </Alert>
          )}

          {detail && (
            <div className="provider-chips">
              <Button
                size="sm"
                variant={tab === 'plan' ? 'primary' : 'ghost'}
                onClick={() => setTab('plan')}
              >
                Plan
              </Button>
              <Button
                size="sm"
                variant={tab === 'prices' ? 'primary' : 'ghost'}
                onClick={() => setTab('prices')}
              >
                Prices ({detail.prices.length})
              </Button>
              <Button
                size="sm"
                variant={tab === 'entitlements' ? 'primary' : 'ghost'}
                onClick={() => setTab('entitlements')}
              >
                Entitlements ({detail.entitlements.length})
              </Button>
            </div>
          )}

          {tab === 'plan' && <PlanForm draft={draft} set={set} locked={detail !== null} />}

          {/*
            On the plan tab rather than in the entitlement list. Credits are the entitlement
            changed most often and the one people come looking for; making somebody know that
            they are modelled as a metered feature before they can find the number is a
            requirement nobody should have.

            Only once the plan exists — there are no entitlements to amend before it is created.
          */}
          {tab === 'plan' && detail && (
            <AiCreditsCard api={api} plan={detail} onChanged={reloadDetail} />
          )}

          {tab === 'prices' && detail && (
            <PricesTab api={api} plan={detail} onChanged={reloadDetail} />
          )}

          {tab === 'entitlements' && detail && (
            <EntitlementsTab api={api} plan={detail} onChanged={reloadDetail} />
          )}
        </div>
      )}
    </Drawer>
  );
}

function PlanForm({
  draft,
  set,
  locked,
}: {
  draft: ProviderPlanInput;
  set: (patch: Partial<ProviderPlanInput>) => void;
  locked: boolean;
}) {
  return (
    <div className="cma-stack">
      <Field label="Name" required>
        {(id) => (
          <Input id={id} value={draft.name} onChange={(e) => set({ name: e.target.value })} />
        )}
      </Field>

      <Field
        label="Plan key"
        required
        hint={
          locked
            ? 'Checkout links and integrations refer to this. Changing it breaks them.'
            : 'Lowercase, no spaces — checkout links and integrations refer to this.'
        }
      >
        {(id) => (
          <Input
            id={id}
            value={draft.planKey}
            onChange={(e) => set({ planKey: e.target.value })}
          />
        )}
      </Field>

      <Field label="Description">
        {(id) => (
          <Textarea
            id={id}
            rows={2}
            value={draft.description ?? ''}
            onChange={(e) => set({ description: e.target.value })}
          />
        )}
      </Field>

      <div className="cma-grid cma-grid--2">
        <Field label="Tier">
          {(id) => (
            <Select id={id} value={draft.tier} onChange={(e) => set({ tier: e.target.value })}>
              <option value="Free">Free</option>
              <option value="Starter">Starter</option>
              <option value="Professional">Professional</option>
              <option value="Enterprise">Enterprise</option>
            </Select>
          )}
        </Field>

        <Field label="Product" hint="Which application this sells, e.g. crm.">
          {(id) => (
            <Input
              id={id}
              value={draft.applicationKey ?? ''}
              onChange={(e) => set({ applicationKey: e.target.value })}
            />
          )}
        </Field>

        <Field label="Seats included">
          {(id) => (
            <Input
              id={id}
              inputMode="numeric"
              value={String(draft.includedSeats)}
              onChange={(e) => set({ includedSeats: Number(e.target.value) || 0 })}
            />
          )}
        </Field>

        <Field label="Seat cap" hint="Empty means no limit.">
          {(id) => (
            <Input
              id={id}
              inputMode="numeric"
              value={draft.maximumSeats == null ? '' : String(draft.maximumSeats)}
              onChange={(e) =>
                set({ maximumSeats: e.target.value === '' ? null : Number(e.target.value) })
              }
            />
          )}
        </Field>

        <Field label="Trial days">
          {(id) => (
            <Input
              id={id}
              inputMode="numeric"
              value={String(draft.trialDays)}
              onChange={(e) => set({ trialDays: Number(e.target.value) || 0 })}
            />
          )}
        </Field>

        <Field label="Order in the list">
          {(id) => (
            <Input
              id={id}
              inputMode="numeric"
              value={String(draft.displayOrder)}
              onChange={(e) => set({ displayOrder: Number(e.target.value) || 0 })}
            />
          )}
        </Field>
      </div>

      <Toggle
        label="Offer a trial"
        checked={draft.isTrialAvailable}
        onChange={(v) => set({ isTrialAvailable: v })}
      />

      <Toggle
        label="A trial needs a payment method up front"
        checked={draft.trialRequiresPaymentMethod}
        onChange={(v) => set({ trialRequiresPaymentMethod: v })}
      />

      <Toggle
        label="Show on the public pricing page"
        checked={draft.isPubliclyListed}
        onChange={(v) => set({ isPubliclyListed: v })}
      />

      <Toggle
        label="This is a bundle of products"
        checked={draft.isBundle}
        onChange={(v) => set({ isBundle: v })}
      />

      <Toggle
        label="This is an add-on, not a plan on its own"
        checked={draft.isAddOn}
        onChange={(v) => set({ isAddOn: v })}
      />

      {draft.isBundle && (
        <Field label="Products in the bundle" hint="Comma-separated application keys.">
          {(id) => (
            <Input
              id={id}
              value={draft.bundledApplicationKeys ?? ''}
              onChange={(e) => set({ bundledApplicationKeys: e.target.value })}
            />
          )}
        </Field>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────── prices

const emptyPrice: ProviderPlanPriceInput = {
  currency: 'SGD',
  interval: 'Monthly',
  baseAmount: 0,
  perSeatAmount: 0,
  setupFee: 0,
  taxBehavior: 'exclusive',
  effectiveFromUtc: null,
  effectiveUntilUtc: null,
};

function PricesTab({
  api,
  plan,
  onChanged,
}: {
  api: ApiClient;
  plan: ProviderPlanDetail;
  onChanged: () => void | Promise<void>;
}) {
  const provider = useMemo(() => providerApi(api), [api]);
  const toast = useToast();

  const [draft, setDraft] = useState<ProviderPlanPriceInput | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const set = (patch: Partial<ProviderPlanPriceInput>) =>
    setDraft((current) => (current ? { ...current, ...patch } : current));

  const save = useMutation(
    () =>
      editingId
        ? provider.updatePlanPrice(editingId, draft!).then(() => undefined)
        : provider.addPlanPrice(plan.id, draft!).then(() => undefined),
    {
      onSuccess: async () => {
        toast.success(editingId ? 'Price corrected' : 'Price added');
        setDraft(null);
        setEditingId(null);
        await onChanged();
      },
      onError: (failure) => setError(failure.message),
    },
  );

  const retire = useMutation((priceId: string) => provider.retirePlanPrice(priceId), {
    onSuccess: async () => {
      toast.success('Price retired');
      await onChanged();
    },
    onError: (failure) => setError(failure.message),
  });

  return (
    <div className="cma-stack">
      {error && (
        <Alert tone="danger" title="That could not be saved">
          {error}
        </Alert>
      )}

      <Alert tone="info">
        A price is added, not edited. A subscription remembers the exact price it was sold on, so
        a new price takes effect on its own date and existing customers move to it at their next
        renewal — rather than being re-priced the moment somebody types a number.
      </Alert>

      {plan.prices.length === 0 ? (
        <Alert tone="warning" title="No price yet">
          This plan cannot be sold until it has one.
        </Alert>
      ) : (
        plan.prices.map((price) => (
          <Card key={price.id}>
            <div className="cma-stack cma-stack--tight">
              <div className="provider-actionrow">
                <div>
                  <div className="provider-strong">
                    {price.currency} · {price.interval}
                  </div>
                  <div className="provider-muted">
                    {formatMoney(price.baseAmount, price.currency)} base +{' '}
                    {formatMoney(price.perSeatAmount, price.currency)} per seat
                    {price.setupFee > 0
                      ? ` + ${formatMoney(price.setupFee, price.currency)} setup`
                      : ''}{' '}
                    · tax {price.taxBehavior}
                  </div>
                </div>

                <div className="provider-chips">
                  {price.isInForce && <Badge tone="success">In force</Badge>}
                  {!price.isActive && <Badge tone="neutral">Retired</Badge>}
                  {price.isInUse && <Badge tone="info">Sold on</Badge>}
                </div>
              </div>

              <div className="provider-linerow">
                <span className="provider-muted">Applies</span>
                <span>
                  from {formatDate(price.effectiveFromUtc)}
                  {price.effectiveUntilUtc ? ` until ${formatDate(price.effectiveUntilUtc)}` : ''}
                </span>
              </div>

              <div className="provider-actionrow">
                <span className="provider-muted">
                  {price.isInUse
                    ? 'A customer is on this price, so it can no longer be changed.'
                    : 'Nothing has been sold on this price yet.'}
                </span>

                <div className="provider-chips">
                  <Button
                    size="sm"
                    onClick={() => {
                      setError(null);
                      setEditingId(null);
                      setDraft({
                        currency: price.currency,
                        interval: price.interval,
                        baseAmount: price.baseAmount,
                        perSeatAmount: price.perSeatAmount,
                        setupFee: price.setupFee,
                        taxBehavior: price.taxBehavior,
                        effectiveFromUtc: null,
                        effectiveUntilUtc: null,
                      });
                    }}
                  >
                    Supersede
                  </Button>

                  {!price.isInUse && (
                    <Button
                      size="sm"
                      onClick={() => {
                        setError(null);
                        setEditingId(price.id);
                        setDraft({
                          currency: price.currency,
                          interval: price.interval,
                          baseAmount: price.baseAmount,
                          perSeatAmount: price.perSeatAmount,
                          setupFee: price.setupFee,
                          taxBehavior: price.taxBehavior,
                          effectiveFromUtc: price.effectiveFromUtc,
                          effectiveUntilUtc: price.effectiveUntilUtc ?? null,
                        });
                      }}
                    >
                      Correct
                    </Button>
                  )}

                  {price.isActive && (
                    <Button
                      size="sm"
                      variant="danger"
                      isLoading={retire.isRunning}
                      onClick={() => void retire.run(price.id)}
                    >
                      Retire
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </Card>
        ))
      )}

      {draft === null ? (
        <Button
          onClick={() => {
            setError(null);
            setEditingId(null);
            setDraft(emptyPrice);
          }}
        >
          Add a price
        </Button>
      ) : (
        <Card>
          <div className="cma-stack">
            <div className="provider-strong">
              {editingId ? 'Correct this price' : 'New price'}
            </div>

            <div className="cma-grid cma-grid--2">
              <Field label="Currency" required>
                {(id) => (
                  <Input
                    id={id}
                    value={draft.currency}
                    maxLength={3}
                    onChange={(e) => set({ currency: e.target.value.toUpperCase() })}
                  />
                )}
              </Field>

              <Field label="Interval">
                {(id) => (
                  <Select
                    id={id}
                    value={draft.interval}
                    onChange={(e) => set({ interval: e.target.value })}
                  >
                    <option value="Monthly">Monthly</option>
                    <option value="Quarterly">Quarterly</option>
                    <option value="Annual">Annual</option>
                    <option value="Custom">Custom</option>
                  </Select>
                )}
              </Field>

              <Field label="Base amount" hint="Charged regardless of seat count.">
                {(id) => (
                  <Input
                    id={id}
                    inputMode="decimal"
                    value={String(draft.baseAmount)}
                    onChange={(e) => set({ baseAmount: Number(e.target.value) || 0 })}
                  />
                )}
              </Field>

              <Field label="Per seat">
                {(id) => (
                  <Input
                    id={id}
                    inputMode="decimal"
                    value={String(draft.perSeatAmount)}
                    onChange={(e) => set({ perSeatAmount: Number(e.target.value) || 0 })}
                  />
                )}
              </Field>

              <Field label="Setup fee">
                {(id) => (
                  <Input
                    id={id}
                    inputMode="decimal"
                    value={String(draft.setupFee)}
                    onChange={(e) => set({ setupFee: Number(e.target.value) || 0 })}
                  />
                )}
              </Field>

              <Field label="Tax" hint="Whether the amount already contains GST/SST.">
                {(id) => (
                  <Select
                    id={id}
                    value={draft.taxBehavior}
                    onChange={(e) => set({ taxBehavior: e.target.value })}
                  >
                    <option value="exclusive">Added on top</option>
                    <option value="inclusive">Already included</option>
                  </Select>
                )}
              </Field>
            </div>

            <Field
              label="In force from"
              hint="Leave empty to start now. Any price it replaces is closed at the same moment."
            >
              {(id) => (
                <Input
                  id={id}
                  type="date"
                  value={draft.effectiveFromUtc?.slice(0, 10) ?? ''}
                  onChange={(e) =>
                    set({
                      effectiveFromUtc: e.target.value
                        ? new Date(`${e.target.value}T00:00:00Z`).toISOString()
                        : null,
                    })
                  }
                />
              )}
            </Field>

            <div className="provider-chips">
              <Button
                variant="primary"
                isLoading={save.isRunning}
                onClick={() => {
                  setError(null);
                  void save.run(undefined);
                }}
              >
                {editingId ? 'Save the correction' : 'Add the price'}
              </Button>

              <Button
                variant="ghost"
                onClick={() => {
                  setDraft(null);
                  setEditingId(null);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────── entitlements


/**
 * The AI credit allowance, on the plan rather than buried in its entitlements.
 *
 * It is one of thirteen entitlements and the one most often changed, so it gets a field of its
 * own here — somebody setting what a plan includes should not have to know that credits are
 * modelled as a metered feature to find the number.
 *
 * Saved through the entitlement endpoint, which replaces the list as a whole: every other row
 * is sent back exactly as it came so nothing else moves.
 */
const AI_FEATURE_KEY = 'platform.ai_assistant';

function AiCreditsCard({
  api,
  plan,
  onChanged,
}: {
  api: ApiClient;
  plan: ProviderPlanDetail;
  onChanged: () => void | Promise<void>;
}) {
  const provider = useMemo(() => providerApi(api), [api]);
  const toast = useToast();

  const existing = plan.entitlements.find((e) => e.featureKey === AI_FEATURE_KEY);

  const [credits, setCredits] = useState(
    existing?.isEnabled && existing.limitValue ? String(existing.limitValue) : '',
  );

  const save = useMutation(
    () => {
      const wanted = credits.trim();
      const limit = wanted === '' ? 0 : Number(wanted);

      const rows: ProviderPlanEntitlementInput[] = plan.entitlements.map((e) => ({
        featureKey: e.featureKey,
        displayName: e.displayName,
        applicationKey: e.applicationKey,
        isEnabled: e.isEnabled,
        limitValue: e.limitValue ?? null,
        resetInterval: e.resetInterval,
        overageBehavior: e.overageBehavior,
        overageUnitPrice: e.overageUnitPrice ?? null,
      }));

      const index = rows.findIndex((r) => r.featureKey === AI_FEATURE_KEY);

      const updated: ProviderPlanEntitlementInput = {
        featureKey: AI_FEATURE_KEY,
        displayName: 'AI assistant credits',
        applicationKey: 'platform',
        // Nothing a month is the same as not including it, and saying so keeps the two from
        // drifting apart: an enabled feature with a limit of nothing refuses on the first
        // question, which reads as broken rather than as not bought.
        isEnabled: limit > 0,
        limitValue: limit > 0 ? limit : 0,
        resetInterval: 'Monthly',
        // Blocked rather than billed as overage. A customer who has spent their allowance
        // should find the assistant quiet, not find an invoice for it.
        overageBehavior: 'Block',
        overageUnitPrice: null,
      };

      if (index >= 0) rows[index] = updated;
      else rows.push(updated);

      return provider.setPlanEntitlements(plan.id, rows);
    },
    {
      onSuccess: async () => {
        toast.success(
          'Saved',
          'Everybody on this plan gets it from their next monthly renewal.',
        );
        await onChanged();
      },
      onError: (failure) => toast.error('Could not save', failure.message),
    },
  );

  return (
    <Card title="AI assistant">
      <div className="cma-stack cma-stack--tight">
        <Field
          label="Credits a month"
          hint="Empty or nothing means this plan does not include the assistant. One customer can still be given a different figure under Customers → AI credits."
        >
          {(id) => (
            <Input
              id={id}
              type="number"
              min={0}
              value={credits}
              placeholder="none"
              onChange={(event) => setCredits(event.target.value)}
            />
          )}
        </Field>

        <div className="cma-row" style={{ gap: 8 }}>
          <Button
            size="sm"
            isLoading={save.isRunning}
            onClick={() => void save.run(undefined as never)}
          >
            Save
          </Button>

          <span className="provider-muted" style={{ alignSelf: 'center' }}>
            {existing?.isEnabled && existing.limitValue
              ? `Currently ${existing.limitValue.toLocaleString()} a month.`
              : 'Currently not included.'}
          </span>
        </div>
      </div>
    </Card>
  );
}

function EntitlementsTab({
  api,
  plan,
  onChanged,
}: {
  api: ApiClient;
  plan: ProviderPlanDetail;
  onChanged: () => void | Promise<void>;
}) {
  const provider = useMemo(() => providerApi(api), [api]);
  const toast = useToast();

  const [rows, setRows] = useState<ProviderPlanEntitlementInput[]>(() =>
    plan.entitlements.map((e) => ({
      featureKey: e.featureKey,
      displayName: e.displayName,
      applicationKey: e.applicationKey,
      isEnabled: e.isEnabled,
      limitValue: e.limitValue ?? null,
      resetInterval: e.resetInterval,
      overageBehavior: e.overageBehavior,
      overageUnitPrice: e.overageUnitPrice ?? null,
    })),
  );

  const [error, setError] = useState<string | null>(null);

  /**
   * Which entitlements to show.
   *
   * A plan carries thirteen of these and each is a full card, so finding one meant scrolling
   * past twelve. Filtering rather than paging because the list is edited as a whole: every row
   * stays in `rows` and is saved whether or not it is on screen.
   */
  const [entitlementFilter, setEntitlementFilter] = useState('');

  const term = entitlementFilter.trim().toLowerCase();

  const visible = rows
    .map((row, index) => ({ row, index }))
    .filter(({ row }) =>
      term.length === 0 ||
      row.featureKey.toLowerCase().includes(term) ||
      (row.displayName ?? '').toLowerCase().includes(term));

  const set = (index: number, patch: Partial<ProviderPlanEntitlementInput>) =>
    setRows((current) => current.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  const save = useMutation(() => provider.setPlanEntitlements(plan.id, rows), {
    onSuccess: async () => {
      toast.success('Entitlements saved', 'Existing subscribers pick these up at their next renewal.');
      await onChanged();
    },
    onError: (failure) => setError(failure.message),
  });

  return (
    <div className="cma-stack">
      {error && (
        <Alert tone="danger" title="That could not be saved">
          {error}
        </Alert>
      )}

      <Alert tone="info">
        What the plan grants, replaced as a whole. A feature removed here disappears from the
        plan. Nobody already subscribed loses anything until their next renewal rebuilds their
        entitlements.
      </Alert>

      <div className="provider-toolbar">
        <Input
          value={entitlementFilter}
          placeholder="Find a feature — try AI, seats, storage…"
          aria-label="Filter entitlements"
          onChange={(event) => setEntitlementFilter(event.target.value)}
          style={{ minWidth: 260 }}
        />
        <span className="provider-muted" style={{ alignSelf: 'center' }}>
          {term ? `${visible.length} of ${rows.length}` : `${rows.length} in this plan`}
        </span>
      </div>

      {visible.map(({ row, index }) => (
        <Card key={index}>
          <div className="cma-stack cma-stack--tight">
            <div className="cma-grid cma-grid--2">
              <Field label="Feature key" required>
                {(id) => (
                  <Input
                    id={id}
                    value={row.featureKey}
                    placeholder="crm.forecasting"
                    onChange={(e) => set(index, { featureKey: e.target.value })}
                  />
                )}
              </Field>

              <Field label="Shown as">
                {(id) => (
                  <Input
                    id={id}
                    value={row.displayName}
                    onChange={(e) => set(index, { displayName: e.target.value })}
                  />
                )}
              </Field>

              <Field label="Product">
                {(id) => (
                  <Input
                    id={id}
                    value={row.applicationKey}
                    onChange={(e) => set(index, { applicationKey: e.target.value })}
                  />
                )}
              </Field>

              <Field label="Limit" hint="Empty means unlimited.">
                {(id) => (
                  <Input
                    id={id}
                    inputMode="numeric"
                    value={row.limitValue == null ? '' : String(row.limitValue)}
                    onChange={(e) =>
                      set(index, {
                        limitValue: e.target.value === '' ? null : Number(e.target.value),
                      })
                    }
                  />
                )}
              </Field>

              <Field label="Limit resets">
                {(id) => (
                  <Select
                    id={id}
                    value={row.resetInterval}
                    onChange={(e) => set(index, { resetInterval: e.target.value })}
                  >
                    <option value="Never">Never</option>
                    <option value="Daily">Daily</option>
                    <option value="Monthly">Monthly</option>
                    <option value="Annual">Annual</option>
                  </Select>
                )}
              </Field>

              <Field label="Over the limit">
                {(id) => (
                  <Select
                    id={id}
                    value={row.overageBehavior}
                    onChange={(e) => set(index, { overageBehavior: e.target.value })}
                  >
                    <option value="Block">Block</option>
                    <option value="Warn">Warn</option>
                    <option value="Bill">Bill for it</option>
                  </Select>
                )}
              </Field>

              {row.overageBehavior === 'Bill' && (
                <Field label="Price per unit over" required>
                  {(id) => (
                    <Input
                      id={id}
                      inputMode="decimal"
                      value={row.overageUnitPrice == null ? '' : String(row.overageUnitPrice)}
                      onChange={(e) =>
                        set(index, {
                          overageUnitPrice:
                            e.target.value === '' ? null : Number(e.target.value),
                        })
                      }
                    />
                  )}
                </Field>
              )}
            </div>

            <div className="provider-actionrow">
              <Toggle
                label="Granted"
                checked={row.isEnabled}
                onChange={(v) => set(index, { isEnabled: v })}
              />

              <Button
                size="sm"
                variant="danger"
                onClick={() => setRows((current) => current.filter((_, i) => i !== index))}
              >
                Remove
              </Button>
            </div>
          </div>
        </Card>
      ))}

      <div className="provider-chips">
        <Button
          onClick={() =>
            setRows((current) => [
              ...current,
              {
                featureKey: '',
                displayName: '',
                applicationKey: plan.applicationKey ?? 'platform',
                isEnabled: true,
                limitValue: null,
                resetInterval: 'Never',
                overageBehavior: 'Block',
                overageUnitPrice: null,
              },
            ])
          }
        >
          Add an entitlement
        </Button>

        <Button
          variant="primary"
          isLoading={save.isRunning}
          onClick={() => {
            setError(null);
            void save.run(undefined);
          }}
        >
          Save entitlements
        </Button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────── shared

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="provider-muted" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      {label}
    </label>
  );
}

function Tile({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: 'warning' | 'danger';
}) {
  return (
    <Card>
      <div className="provider-tile__label">{label}</div>
      <div className={`provider-tile__value${tone ? ` provider-tile__value--${tone}` : ''}`}>
        {value.toLocaleString()}
      </div>
    </Card>
  );
}
